/** Training now (DESIGN §41.3): the team's day as it happens, read from `GET /web/api/training`.
 * The twin of `TrainingBoard` in WeightsParse; the two keep the same cases. */
import { allSkipped, norm, plural, recordOf, unitSuffix } from "./plan.js";
import { runLoad, runType } from "./running.js";
import { isDistance, raceTime, speedNumber, weightNumber } from "./sets.js";
import { shownUnit, shownWeight } from "../prefs.js";
import type { Exercise, RecordMark, Session, SetLog, TrainingDay, TrainingEntry } from "./types.js";

/** 2001-01-01T00:00:00Z, where the phone's dates count from, in Unix milliseconds. */
const REFERENCE_MS = 978_307_200_000;
const MINUTE = 60_000;
export const QUIET_AFTER_MS = 15 * MINUTE;
export const LEFT_OPEN_AFTER_MS = 3 * 60 * MINUTE;
export const FEED_MAX = 40;

/** `skipped`: finished with every set skipped (§61.8). */
export type TrainingState = "training" | "quiet" | "done" | "skipped" | "leftOpen" | "notStarted";

export const STATE_WORD: Record<TrainingState, string> = {
  training: "Training",
  quiet: "Quiet",
  done: "Done",
  skipped: "Skipped",
  leftOpen: "Left open",
  notStarted: "Not started",
};

const STATE_ORDER: TrainingState[] = ["training", "quiet", "done", "skipped", "leftOpen", "notStarted"];

export function phoneDate(seconds: number): Date {
  return new Date(REFERENCE_MS + seconds * 1000);
}

export interface Slot {
  exerciseID: string;
  setIndex: number;
  done: boolean;
  /** Answered as skipped (§61.8): drawn dashed, not done. */
  skipped: boolean;
  next: boolean;
}

export interface ProgressBlock {
  letter: string;
  slots: Slot[];
}

export interface Progress {
  blocks: ProgressBlock[];
  done: number;
  skipped: number;
  total: number;
  /** The day's other sessions not read into sets: "Tempo". */
  notLogged: string[];
}

export interface LastSet {
  text: string;
  record: string | null;
  at: Date | null;
}

export interface BoardRow {
  athleteID: string;
  name: string;
  state: TrainingState;
  word: string;
  /** "Back squat, set 4", "No sets for 17 min", "Done in 52 min". */
  detail: string;
  progress: Progress | null;
  last: LastSet | null;
  /** When they last did something: the last activity, or the finish. */
  at: Date | null;
}

export interface FastestRep {
  athleteID: string;
  name: string;
  speed: number;
  kg: number | null;
}

export interface Fastest {
  meters: number;
  reps: FastestRep[];
}

export interface BoardGroup {
  /** null for Other. */
  group: string | null;
  title: string;
  rows: BoardRow[];
  fastest: Fastest[];
}

export interface FeedItem {
  key: string;
  at: Date;
  athleteID: string;
  name: string;
  what: string;
  value: string | null;
  record: string | null;
}

export interface Counts {
  training: number;
  quiet: number;
  done: number;
  skipped: number;
  leftOpen: number;
  notStarted: number;
}

export interface Board {
  date: string;
  live: boolean;
  startedBy: string | null;
  startedAt: Date | null;
  counts: Counts;
  groups: BoardGroup[];
  feed: FeedItem[];
  bests: number;
  /** Anything planned or anyone with a workout today. */
  empty: boolean;
}


const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, undefined, { sensitivity: "base", numeric: true });

function exercisesOf(session: Session): Map<string, Exercise> {
  const out = new Map<string, Exercise>();
  for (const b of session.blocks ?? []) for (const e of b.exercises ?? []) if (!out.has(norm(e.id))) out.set(norm(e.id), e);
  return out;
}

export function isResistedRun(exercise: Exercise | undefined, name: string): boolean {
  return !!exercise?.isResisted || runType(name) === "resisted";
}

/** DESIGN §41.4: a resisted run or a time trial; its reps are meant to carry a time. */
export function asksForNumbers(exercise: Exercise | undefined, name: string): boolean {
  return isResistedRun(exercise, name) || /\b(tt|time[\s-]+trials?)\b/i.test(name);
}

function nameOf(log: SetLog, exercise: Exercise | undefined): string {
  return (exercise?.name?.trim() || log.exerciseKey || "").trim();
}

function isRun(log: SetLog, exercise: Exercise | undefined): boolean {
  return isDistance(log) || exercise?.target?.unit === "meters";
}

function weightText(weight: number, unit: string | null | undefined): string {
  return weightNumber(shownWeight(weight, unit)) + " " + shownUnit(unit);
}

/** A set as the athlete's lists write it: `140 kg × 5`, `8 reps`, `31.8 s`, `5.9 m/s · 2.31 s · 8 kg`, `Done`, `skipped`. */
export function lastSetText(log: SetLog, exercise?: Exercise): string {
  if (log.skipped) return "skipped";
  const name = nameOf(log, exercise);
  if (isRun(log, exercise)) {
    const timed = log.time != null && log.time > 0;
    const parts = log.partTimes && log.partTimes.length > 1 && log.partTimes.some((t) => t != null) ? log.partTimes : null;
    const time = timed ? raceTime(log.time!) : parts ? parts.map((t) => (t != null ? raceTime(t) : "–")).join(" + ") : null;
    const asks = asksForNumbers(exercise, name);
    if (isResistedRun(exercise, name)) {
      const out: string[] = [];
      if (log.topSpeed != null && log.topSpeed > 0) out.push(speedNumber(log.topSpeed) + " m/s");
      out.push(time ?? "no time");
      if (log.weight != null && log.weight > 0) out.push(weightText(log.weight, log.unit));
      return out.join(" · ");
    }
    if (time) return time;
    return asks ? "no time" : "Done";
  }
  if (exercise && recordOf(exercise) === "done") return "Done";
  const unit = log.measure ?? exercise?.target?.unit ?? "reps";
  if (log.weight != null && log.weight > 0) {
    const w = weightText(log.weight, log.unit);
    return log.amount != null ? w + " × " + log.amount + unitSuffix(unit) : w;
  }
  if (log.amount != null) return unit === "seconds" ? log.amount + " s" : plural(log.amount, "rep", "reps");
  if (log.time != null && log.time > 0) return raceTime(log.time);
  return "Done";
}

/** "Back squat, set 4"; a distance counts reps. */
function slotName(exercise: Exercise | undefined, fallback: string, setIndex: number): string {
  const run = exercise?.target?.unit === "meters";
  return (exercise?.name?.trim() || fallback) + ", " + (run ? "rep " : "set ") + (setIndex + 1);
}

function logTime(log: SetLog): number | null {
  return typeof log.completedAt === "number" ? REFERENCE_MS + log.completedAt * 1000 : null;
}

/** The most recent log: latest `completedAt`, the later in the list on a tie or without one. */
function latestLog(logs: SetLog[]): SetLog | null {
  let best: SetLog | null = null;
  let bestAt = -Infinity;
  for (const log of logs) {
    const at = logTime(log) ?? -Infinity;
    if (!best || at >= bestAt) {
      best = log;
      bestAt = at;
    }
  }
  return best;
}

/** The latest of `startedAt` and its logs' `completedAt`; the row's update time when it has neither. */
function lastActivity(entry: TrainingEntry): number | null {
  let at = typeof entry.session.startedAt === "number" ? REFERENCE_MS + entry.session.startedAt * 1000 : null;
  for (const log of entry.session.logs ?? []) {
    const t = logTime(log);
    if (t != null && (at == null || t > at)) at = t;
  }
  if (at == null && entry.updatedAt) {
    const t = Date.parse(entry.updatedAt);
    if (!isNaN(t)) at = t;
  }
  return at;
}

const startOf = (s: Session) => (typeof s.startedAt === "number" ? REFERENCE_MS + s.startedAt * 1000 : null);
const finishOf = (s: Session) => (typeof s.finishedAt === "number" ? REFERENCE_MS + s.finishedAt * 1000 : null);

export interface NextSet {
  exerciseID: string;
  setIndex: number;
  name: string;
}

/** Every block's slots, prep and the day's other parts lettered with the rest (§61.6), set by set as the phone lists them, and the next set (§41.3). */
export function progressOf(session: Session): { progress: Progress; next: NextSet | null } {
  const logs = session.logs ?? [];
  const slotKey = (l: SetLog) => norm(l.exerciseID) + "#" + (l.setIndex ?? 0);
  const done = new Set(logs.filter((l) => !l.skipped).map(slotKey));
  const skipped = new Set(logs.filter((l) => l.skipped).map(slotKey));
  const blocks = session.blocks ?? [];
  const letters = blocks.map((_, i) => String.fromCharCode(65 + (i % 26)));
  const slots = blocks.map((b) => {
    const out: (Slot & { exercise: Exercise })[] = [];
    for (let set = 0; set < Math.max(0, b.sets ?? 0); set++)
      for (const e of b.exercises ?? []) {
        const key = norm(e.id) + "#" + set;
        const isDone = done.has(key);
        out.push({ exerciseID: norm(e.id), setIndex: set, done: isDone, skipped: !isDone && skipped.has(key), next: false, exercise: e });
      }
    return out;
  });

  const last = latestLog(logs);
  let pick: (typeof slots)[number][number] | undefined;
  if (last) {
    const id = norm(last.exerciseID);
    const b = slots.findIndex((s) => s.some((x) => x.exerciseID === id));
    if (b >= 0) {
      const at = slots[b]!.findIndex((x) => x.exerciseID === id && x.setIndex === (last.setIndex ?? 0));
      const from = at >= 0 ? at + 1 : slots[b]!.length;
      pick = slots[b]!.slice(from).find((x) => !x.done && !x.skipped);
      for (let i = b + 1; !pick && i < slots.length; i++) pick = slots[i]!.find((x) => !x.done && !x.skipped);
    }
  }
  if (!pick) pick = slots.flat().find((x) => !x.done && !x.skipped);
  if (pick) pick.next = true;

  const logged = new Set(blocks.map((b) => b.loggedFrom).filter((x): x is string => !!x));
  const notLogged = (session.alsoToday ?? []).map((a) => a.label).filter((l) => l && !logged.has(l));
  const flat = slots.flat();
  return {
    progress: {
      blocks: slots.map((s, i) => ({ letter: letters[i]!, slots: s.map(({ exerciseID, setIndex, done, skipped, next }) => ({ exerciseID, setIndex, done, skipped, next })) })),
      done: flat.filter((x) => x.done).length,
      skipped: flat.filter((x) => x.skipped).length,
      total: flat.length,
      notLogged,
    },
    next: pick ? { exerciseID: pick.exerciseID, setIndex: pick.setIndex, name: slotName(pick.exercise, "", pick.setIndex) } : null,
  };
}

/** `17 min`, `1 h 5 min`. */
export function minutesText(ms: number): string {
  const minutes = Math.max(0, Math.floor(ms / MINUTE));
  if (minutes < 60) return minutes + " min";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h + " h" + (m ? " " + m + " min" : "");
}

/** `3:42 pm`. */
export function clockText(at: Date): string {
  return at.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }).replace(/[\s ]*([AP])M$/i, (_, a: string) => " " + a.toLowerCase() + "m");
}

/** A row's time: `Just now`, `3 min ago`, `at 3:42 pm`. */
export function rowAgoText(at: Date, now: Date): string {
  const ms = now.getTime() - at.getTime();
  if (ms < MINUTE) return "Just now";
  if (ms < 60 * MINUTE) return Math.floor(ms / MINUTE) + " min ago";
  return "at " + clockText(at);
}

/** `Now` under a minute, `n min` under an hour, else the clock time. */
export function agoText(at: Date, now: Date): string {
  const ms = now.getTime() - at.getTime();
  if (ms < MINUTE) return "Now";
  if (ms < 60 * MINUTE) return Math.floor(ms / MINUTE) + " min";
  return clockText(at);
}

function recordFor(records: RecordMark[] | undefined, log: SetLog): string | null {
  if (!log.id) return null;
  return records?.find((r) => norm(r.logID) === norm(log.id))?.label ?? null;
}

function effortText(session: Session): string | null {
  const start = startOf(session);
  const finish = finishOf(session);
  const parts: string[] = [];
  if (start != null && finish != null) parts.push(minutesText(finish - start));
  if (session.effort != null && session.effort > 0) parts.push("felt " + session.effort + " of 10");
  return parts.length ? parts.join(" · ") : null;
}

const sessionTitle = (s: Session) => (s.title ?? "").trim() || "the workout";

/** The athletes' best top speed today per distance, on resisted reps, the three fastest. */
function fastestOf(entries: { entry: TrainingEntry; name: string }[]): Fastest[] {
  const byMeters = new Map<number, Map<string, FastestRep>>();
  for (const { entry, name } of entries) {
    const exercises = exercisesOf(entry.session);
    for (const log of entry.session.logs ?? []) {
      const ex = exercises.get(norm(log.exerciseID));
      if (log.skipped || !isRun(log, ex) || !isResistedRun(ex, nameOf(log, ex))) continue;
      const meters = log.amount ?? ex?.target?.amount;
      if (meters == null || !(meters > 0) || log.topSpeed == null || !(log.topSpeed > 0)) continue;
      let athletes = byMeters.get(meters);
      if (!athletes) byMeters.set(meters, (athletes = new Map()));
      const now = athletes.get(entry.athleteID);
      if (!now || log.topSpeed > now.speed) athletes.set(entry.athleteID, { athleteID: entry.athleteID, name, speed: log.topSpeed, kg: runLoad(log) });
    }
  }
  return [...byMeters.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([meters, athletes]) => ({ meters, reps: [...athletes.values()].sort((a, b) => b.speed - a.speed || byName(a, b)).slice(0, 3) }));
}

/** `Fastest today, resisted 10 m · Theo 6.1 m/s (6 kg) · Maya 6.0 m/s (8 kg)`, weights in the viewer's unit. */
export function fastestText(f: Fastest): string {
  const reps = f.reps.map((r) => r.name + " " + speedNumber(r.speed) + " m/s" + (r.kg != null ? " (" + weightText(r.kg, "kg") + ")" : ""));
  return ["Fastest today, resisted " + f.meters + " m", ...reps].join(" · ");
}

/** "Maya started first at 3:42 pm". */
export function startedText(board: Pick<Board, "startedBy" | "startedAt">): string | null {
  return board.startedBy && board.startedAt ? board.startedBy + " started first at " + clockText(board.startedAt) : null;
}

/** How many are training now (Training or Quiet): the menu's `● n`. */
export function trainingNow(board: Pick<Board, "counts">): number {
  return board.counts.training + board.counts.quiet;
}

/** The day, read once (DESIGN §41.3). */
export function trainingBoard(day: TrainingDay, now: Date): Board {
  const t = now.getTime();
  const athletes = new Map((day.athletes ?? []).map((a) => [a.id, a]));
  // The server lists each athlete once; a repeat must not make a second row.
  const roster = [...athletes.values()];
  const finished = (day.finished ?? []).filter((e) => athletes.has(e.athleteID) && e.session);
  const finishedIDs = new Set(finished.map((e) => norm(e.sessionID)));
  const training = (day.training ?? []).filter((e) => athletes.has(e.athleteID) && e.session && !finishedIDs.has(norm(e.sessionID)));
  const entries = [...finished, ...training];
  const nameFor = (id: string) => athletes.get(id)!.name;

  // MARK: Who first
  let startedBy: string | null = null;
  let startedAt: number | null = null;
  for (const e of entries) {
    const s = startOf(e.session);
    if (s != null && (startedAt == null || s < startedAt)) {
      startedAt = s;
      startedBy = nameFor(e.athleteID);
    }
  }

  // MARK: Groups planned today, in the plan's order
  const planned: { group: string; titles: string[] }[] = [];
  for (const s of day.sessions ?? []) {
    const g = planned.find((p) => p.group === s.group);
    if (g) g.titles.push(s.title);
    else planned.push({ group: s.group, titles: [s.title] });
  }
  const plannedKeys = new Set(planned.map((p) => p.group));

  // MARK: Each athlete's row
  const rows = new Map<string, { row: BoardRow; entry: TrainingEntry | null }>();
  for (const a of roster) {
    const done = finished.filter((e) => e.athleteID === a.id).sort((x, y) => (finishOf(y.session) ?? 0) - (finishOf(x.session) ?? 0))[0];
    const open = training.filter((e) => e.athleteID === a.id).sort((x, y) => (lastActivity(y) ?? 0) - (lastActivity(x) ?? 0))[0];
    const entry = done ?? open ?? null;
    let state: TrainingState;
    let detail = "";
    let at: number | null = null;
    if (done) {
      state = allSkipped(done.session.logs ?? []) ? "skipped" : "done";
      const start = startOf(done.session);
      const finish = finishOf(done.session);
      at = finish ?? lastActivity(done);
      detail = state === "skipped" ? "Skipped" : start != null && finish != null ? "Done in " + minutesText(finish - start) : "Done";
    } else if (open) {
      at = lastActivity(open);
      const age = at == null ? Infinity : t - at;
      state = age < QUIET_AFTER_MS ? "training" : age < LEFT_OPEN_AFTER_MS ? "quiet" : "leftOpen";
    } else if ((a.groups ?? []).some((g) => plannedKeys.has(g))) {
      state = "notStarted";
    } else continue;

    let progress: Progress | null = null;
    let last: LastSet | null = null;
    if (entry) {
      const read = progressOf(entry.session);
      progress = read.progress;
      if (state !== "training" && state !== "quiet") for (const b of progress.blocks) for (const s of b.slots) s.next = false;
      const log = latestLog(entry.session.logs ?? []);
      if (log) {
        const ex = exercisesOf(entry.session).get(norm(log.exerciseID));
        const lt = logTime(log);
        last = { text: lastSetText(log, ex), record: recordFor(entry.records, log), at: lt != null ? new Date(lt) : null };
      }
      if (state === "training") detail = !log ? "Starting" : (read.next?.name ?? "All sets logged");
      else if (state === "quiet") detail = "No sets for " + minutesText(t - at!);
      else if (state === "leftOpen") detail = "Not finished";
    }
    rows.set(a.id, {
      row: { athleteID: a.id, name: a.name, state, word: STATE_WORD[state], detail, progress, last, at: at != null ? new Date(at) : null },
      entry,
    });
  }

  // MARK: Cards
  const cards = new Map<string, BoardGroup>();
  for (const p of planned) cards.set(p.group, { group: p.group, title: (p.group || "Everyone") + " · " + p.titles.join(" + "), rows: [], fastest: [] });
  const other: BoardGroup = { group: null, title: "Other", rows: [], fastest: [] };
  const cardEntries = new Map<BoardGroup, { entry: TrainingEntry; name: string }[]>();
  for (const a of roster) {
    const r = rows.get(a.id);
    if (!r) continue;
    const g = (a.groups ?? []).find((x) => plannedKeys.has(x));
    const card = g != null ? cards.get(g)! : other;
    card.rows.push(r.row);
    if (r.entry) {
      const list = cardEntries.get(card) ?? [];
      list.push({ entry: r.entry, name: a.name });
      cardEntries.set(card, list);
    }
  }
  const groups = [...cards.values(), ...(other.rows.length ? [other] : [])];
  for (const g of groups) {
    g.rows.sort((a, b) => STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state) || byName(a, b));
    g.fastest = fastestOf(cardEntries.get(g) ?? []);
  }

  // MARK: As it happens
  const feed: FeedItem[] = [];
  let bests = 0;
  for (const e of entries) {
    const name = nameFor(e.athleteID);
    const exercises = exercisesOf(e.session);
    bests += (e.records ?? []).filter((r) => r.label).length;
    (e.session.logs ?? []).forEach((log, i) => {
      const lt = logTime(log);
      if (lt == null) return;
      const ex = exercises.get(norm(log.exerciseID));
      feed.push({
        key: e.sessionID + ":" + i,
        at: new Date(lt),
        athleteID: e.athleteID,
        name,
        what: slotName(ex, log.exerciseKey || "Set", log.setIndex ?? 0),
        value: lastSetText(log, ex),
        record: recordFor(e.records, log),
      });
    });
    const finish = finishedIDs.has(norm(e.sessionID)) ? finishOf(e.session) : null;
    if (finish != null)
      feed.push({ key: e.sessionID + ":finish", at: new Date(finish), athleteID: e.athleteID, name, what: "finished " + sessionTitle(e.session), value: effortText(e.session), record: null });
  }
  feed.sort((a, b) => b.at.getTime() - a.at.getTime() || (a.key.endsWith(":finish") ? -1 : b.key.endsWith(":finish") ? 1 : 0));

  const counts: Counts = { training: 0, quiet: 0, done: 0, skipped: 0, leftOpen: 0, notStarted: 0 };
  for (const { row } of rows.values()) counts[row.state]++;

  return {
    date: day.date,
    live: counts.training + counts.quiet > 0,
    startedBy,
    startedAt: startedAt != null ? new Date(startedAt) : null,
    counts,
    groups,
    feed: feed.slice(0, FEED_MAX),
    bests,
    empty: groups.length === 0,
  };
}
